import { faDownload, faRefresh, faUpload } from '@fortawesome/pro-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { useUser } from 'Core/Hooks/useUser'
import { CertificateInfo, fetchCustomerCA } from 'features/settings'
import { createDataSelectorHook } from 'infra/redux'
import { downloadFile } from 'infra/utils'
import { Button, Label, Tooltip, Typography } from 'procyon-ui'
import React, { useEffect, useState } from 'react'
import { ButtonGroup, CopyText, LabelContent } from 'V2Components'
import UploadUserCAModal from '../../UploadUserCAModal/UploadUserCAModal'

const useSlices = createDataSelectorHook(['tenantprofiles'])

function FrontendCA() {
  const [showUserCAModal, setShowUserCAModal] = useState(false)
  const [activeCertButton, setActiveCertButton] = useState('root-certificate')
  const [csr, setCSR] = useState('')
  const [rootCertificate, setRootCertificate] = useState('')
  const [signedCertificate, setSignedCertificate] = useState('')

  const { slices } = useSlices()
  const { user } = useUser()

  const tenantProfile = slices.tenantprofiles.find(
    (e) => e.ObjectMeta.Name === user.ObjectMeta.Tenant
  )

  const isSignedByCustomerRoot = tenantProfile?.Spec?.IsSignedByCustomerRoot || false

  useEffect(() => {
    fetchCustomerCA().then((data) => {
      setCSR(data.CSR)
      setRootCertificate(data.RootCert)
      setSignedCertificate(data.SignedCert)
    })
  }, [])

  return (
    <div className='flex gap-4'>
      <div className='w-1/2 border-r pr-4 overflow-auto'>
        <LabelContent
          title='Signed By Customer CA'
          content={
            isSignedByCustomerRoot ? (
              <Label variant='success' text='Yes' />
            ) : (
              <Label variant='gray' text='No' />
            )
          }
        />
        <div className='mt-4'>
          <Typography variant='body-regular'>
            <Button
              variant='grayBlue'
              disabled={!csr}
              onClick={() => downloadFile(csr, 'ping-privilege-frontend.csr')}
              icon={faDownload}
            >
              Download CSR
            </Button>
          </Typography>
          {csr && (
            <CopyText
              Component={
                <div className='mt-4 max-w-full md:max-w-[600px] max-h-[60vh] bg-gray-100 overflow-auto rounded'>
                  <Tooltip title='Click to Copy' arrow>
                    <div className='p-2 rounded bg-gray-100 cursor-copy'>
                      <Typography className='!break-all' variant='body-regular'>
                        <code>
                          <pre>{csr}</pre>
                        </code>
                      </Typography>
                    </div>
                  </Tooltip>
                </div>
              }
              messageKey={'CSR'}
              text={csr}
            />
          )}
          {!csr && (
            <div className='mt-4 h-[400px] w-[600px] flex justify-center items-center bg-gray-100 overflow-auto'>
              <Typography
                className='flex items-center justify-between !text-gray-500'
                variant='body-regular'
              >
                <FontAwesomeIcon className='animate-spin mr-1 !text-gray-400' icon={faRefresh} />
                Loading CSR
              </Typography>
            </div>
          )}
          <div className='mt-4 '>
            <Button onClick={() => setShowUserCAModal(true)} icon={faUpload} variant='grayBlue'>
              Upload Customer Signed CA Certificate
            </Button>
          </div>
        </div>
        {showUserCAModal && (
          <UploadUserCAModal csr={csr} onCancel={() => setShowUserCAModal(false)} />
        )}
      </div>
      {isSignedByCustomerRoot && (
        <div className='w-1/2'>
          <Typography variant='h3'>Customer Signed Certificates</Typography>
          <div className='mt-4'>
            <ButtonGroup
              activeButtonKey={activeCertButton}
              onActiveButtonKeyChange={setActiveCertButton}
              buttons={[
                {
                  label: 'Root Certificate',
                  key: 'root-certificate'
                },
                {
                  label: 'Signed Certificate',
                  key: 'signed-certificate'
                }
              ]}
            />
          </div>
          <div className='mt-4'>
            {(!rootCertificate || !signedCertificate) && (
              <Typography variant='body-regular'>Missing Certificates</Typography>
            )}
            {activeCertButton === 'root-certificate' && rootCertificate && (
              <CertificateInfo title='Root Certificate' cert={rootCertificate} />
            )}
            {activeCertButton === 'signed-certificate' && signedCertificate && (
              <CertificateInfo title='Signed Certificate' cert={signedCertificate} />
            )}
          </div>
        </div>
      )}
    </div>
  )
}

export { FrontendCA }
